import INC_BASE_API from 'apis/incentivio-api';
import { useHandleOrderStatus } from 'pages/checkout/checkout-buttons-new/checkout-buttons-new.hooks';
import SpreedlyCheckoutButtonsContext from 'pages/checkout/checkout-buttons-new/checkout-buttons-wrapper/spreedly-checkout-buttons/spreedly-checkout-buttons-container/spreedly-checkout-buttons-provider/spreedly-checkout-buttons.context';
import { useCheckoutPaymentLoading } from 'pages/checkout/checkout.component';
import CheckoutContext from 'pages/checkout/checkout.context';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectPaymentOption } from 'redux/cart/cart.selectors';
import { selectIsLoggedIn } from 'redux/user/user.selectors';
import { IncentivioApiError } from 'util/errors';
import { useIsNewSpreedlyCardFlow } from '../payment-selector/credit-card-panel/credit-card-panel-controller/credit-card-panel-controller.hooks';
import {
  PaymentOptionType,
  SpreedlyPaymentProcessorType,
} from '../payment.constants';
import { NewPaymentContext } from '../payment.context';
import {
  useMakePayment,
  useMakePaymentBody,
  usePaymentGiftCardAsAGuest,
  usePaymentMode,
  usePurchaseEmailGiftCardBody,
  useWallets,
} from '../payment.new.hooks';
import SpreedlyContext from './spreedly.context';
import { useApplePay } from './toast/apple-pay.hooks';
import SpreedlyCVVDialogContext from './spreedly-card/spreedly-cvv-dialog/spreedly-cvv-dialog.context';
import { useSpreedlyCheckoutSdkScriptLoading } from '../spreedly-checkout-sdk/spreedly-checkout-sdk-script-loading.hooks';

export const getSpreedlyPaymentProcessorType = ({ attributes } = {}) =>
  Object.values(SpreedlyPaymentProcessorType).find(
    value => value === attributes?.PAYMENT_PROCESSOR,
  ) ?? SpreedlyPaymentProcessorType.OTHER;

export const useSpreedlyPayments = () => {
  const [cardInfo, setCardInfo] = useState();
  const [spreedlyPaymentProcessorType, setSpreedlyPaymentProcessorType] =
    useState();
  const { initOrderResponse } = useContext(NewPaymentContext);
  useSetWalletSupport(spreedlyPaymentProcessorType);
  const [submitSpreedlyForm, setSubmitSpreedlyForm] = useState(null);
  const [tokenizationSuccess, setTokenizationSuccess] = useState(false);
  const [spreedlyErrorCount, setSpreedlyErrorCount] = useState(0);

  useEffect(() => {
    setSpreedlyPaymentProcessorType(
      getSpreedlyPaymentProcessorType(initOrderResponse),
    );
  }, [initOrderResponse]);

  useEffect(() => {
    setTokenizationSuccess(false);
  }, [initOrderResponse]);

  const { loading: spreedlyScriptLoading, error: spreedlyScriptError } =
    useSpreedlyCheckoutSdkScriptLoading();

  useEffect(() => {
    // Once the script hook gives up (loading turns false) after a load
    // failure, spreedlyScriptLoading alone can't tell the difference from a
    // successful load — leaving downstream code to call into
    // window.SpreedlyHostedFields/SpreedlyThreeDSLifecycle, which never got
    // defined. Throwing here (same pattern as ThreeDS2/ThreeDS2CheckoutSdk)
    // surfaces a deterministic error via the payment error boundary instead.
    if (!!spreedlyScriptError)
      throw new Error(
        `Spreedly script loading error: ${spreedlyScriptError.message}`,
      );
  }, [spreedlyScriptError]);

  const notifySpreedlyError = () => setSpreedlyErrorCount(count => count + 1);

  return {
    cardInfo,
    setCardInfo,
    spreedlyPaymentProcessorType,
    submitSpreedlyForm,
    setSubmitSpreedlyForm,
    spreedlyScriptLoading,
    tokenizationSuccess,
    setTokenizationSuccess,
    spreedlyErrorCount,
    notifySpreedlyError,
  };
};

export const usePlaceOrderWithCard = () => useMakePayment(useMakePaymentBody());

const useHandleCardPaymentSuccess = () => {
  const history = useHistory();
  const { setThreeDSTransactionToken } = useContext(
    SpreedlyCheckoutButtonsContext,
  );

  const trigger = response => {
    if (response.orderStatus === 'PAYMENT_3DS2_AUTHENTICATION_PENDING') {
      setThreeDSTransactionToken(response.externalStatuses.TRANSACTION_TOKEN);
      return;
    }
    history.push('/checkout/thankyou', response);
  };

  return { trigger };
};

export const useCheckoutSpreedlyCardPaymentWithoutModal = () => {
  const { trigger: cardTrigger } = usePlaceOrderWithCard();
  const { refetchInitOrder } = useContext(NewPaymentContext);
  const handleOrderStatus = useHandleOrderStatus();
  const { cardInfo, setCardInfo, submitSpreedlyForm } =
    useContext(SpreedlyContext);
  const addPromise = useCheckoutPaymentLoading(state => state.addPromise);
  const { trigger: cardPaymentSuccessTrigger } = useHandleCardPaymentSuccess();

  const handleSpreeldyPaymentWithoutModalGuestPayment = async data => {
    try {
      const response = await addPromise(cardTrigger(data));
      setCardInfo(null);
      cardPaymentSuccessTrigger(response.data);
    } catch (e) {
      handleOrderStatus(e);
      await addPromise(refetchInitOrder());
    }
  };

  useEffect(() => {
    if (!cardInfo) return;
    handleSpreeldyPaymentWithoutModalGuestPayment({
      paymentToken: cardInfo.token,
      savePaymentInstrument: cardInfo.savePaymentInstrument ?? false,
      isOneTimeTransaction: true,
      cardNickname: cardInfo.cardNickname,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardInfo]);

  const trigger = () => {
    submitSpreedlyForm();
  };

  return { trigger };
};

export const useSpreedlyPaymentsPlaceOrder = () => {
  const { handleCheckWithCVVRecacheDialogAndDoPayment } = useContext(
    SpreedlyCVVDialogContext,
  );
  const addPromise = useCheckoutPaymentLoading(state => state.addPromise);
  const paymentMode = usePaymentMode();
  const paymentOption = useSelector(selectPaymentOption);
  const { trigger: cardTrigger } = usePlaceOrderWithCard();
  const { trigger: spreedlyCardPaymentWithoutModalTrigger } =
    useCheckoutSpreedlyCardPaymentWithoutModal();
  const { trigger: cardPaymentSuccessTrigger } = useHandleCardPaymentSuccess();
  const { trigger: lookupGiftCardTrigger } = usePaymentGiftCardAsAGuest();
  const isNewSpreedlyCardFlow = useIsNewSpreedlyCardFlow();
  const { isSpreedlyFormStep } = useContext(CheckoutContext);
  const history = useHistory();

  let response;

  const trigger = async () => {
    try {
      if (paymentMode === 'NO_PAYMENT') {
        response = await addPromise(cardTrigger());
        history.push('/checkout/thankyou', response.data);
      } else {
        // eslint-disable-next-line default-case
        switch (paymentOption) {
          case PaymentOptionType.CARD:
            if (isNewSpreedlyCardFlow && isSpreedlyFormStep) {
              spreedlyCardPaymentWithoutModalTrigger();
            } else {
              const callback = async () => {
                const response = await addPromise(cardTrigger());
                cardPaymentSuccessTrigger(response.data);
              };
              await handleCheckWithCVVRecacheDialogAndDoPayment(await callback);
            }
            break;

          case PaymentOptionType.PAY_LATER:
          case PaymentOptionType.GIFT_CARD:
            response = await addPromise(cardTrigger());
            history.push('/checkout/thankyou', response.data);
            break;

          case PaymentOptionType.GUEST_CARD:
            spreedlyCardPaymentWithoutModalTrigger();
            break;

          case PaymentOptionType.GUEST_GIFT_CARD:
            const { lookupToken, giftCardNumber } = await addPromise(
              lookupGiftCardTrigger(),
            );
            if (lookupToken) {
              const response = await cardTrigger({
                paymentToken: giftCardNumber,
                isOneTimeTransaction: true,
              });

              history.push('/checkout/thankyou', response.data);
            }
            break;
        }
      }
    } catch (e) {
      // handle errors in caller
      throw e;
    }
  };

  return { trigger };
};

export const useSpreedlyEmailGiftCardPayment = () => {
  const [loading, setLoading] = useState(false);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const body = usePurchaseEmailGiftCardBody();
  const { refetchInitOrder } = useContext(NewPaymentContext);

  const trigger = async paymentToken => {
    try {
      setLoading(true);

      body.paymentToken = paymentToken;

      const response = await INC_BASE_API.post(`/giftcard/purchase`, body, {
        authenticated: isLoggedIn,
      });

      return response;
    } catch (e) {
      refetchInitOrder();
      throw new IncentivioApiError('useSpreedlyEmailGiftCardPayment error', e);
    } finally {
      setLoading(false);
    }
  };

  return { loading, trigger };
};

export const useSpreedlyPurchaseEmailGiftCard = () => {
  const {
    loading: emailGiftCardLoading,
    trigger: triggerEmailGiftCardPayment,
  } = useSpreedlyEmailGiftCardPayment();

  const loading = useMemo(() => emailGiftCardLoading, [emailGiftCardLoading]);

  const trigger = async paymentToken => {
    const resp = await triggerEmailGiftCardPayment(paymentToken);

    return resp;
  };

  return { loading, trigger };
};

export const useSpreedlyPaymentProcessorMatches = () => {
  const spreedlyPaymentContext = useContext(SpreedlyContext);

  return paymentProcessorTypes =>
    paymentProcessorTypes.includes(
      spreedlyPaymentContext?.spreedlyPaymentProcessorType,
    );
};

export const useSetWalletSupport = spreedlyPaymentProcessorType => {
  const { enabled: applePayEnabled, canMakePayments: canMakeApplePayPayments } =
    useApplePay();
  const { setWalletSupport, setLoading } = useWallets();

  useEffect(() => {
    const execute = async () => {
      // eslint-disable-next-line default-case
      switch (spreedlyPaymentProcessorType) {
        case SpreedlyPaymentProcessorType.TOAST:
          setLoading(true);
          const applePay = applePayEnabled && (await canMakeApplePayPayments());
          setWalletSupport(prev => ({
            ...prev,
            applePay,
          }));
          setLoading(false);
          break;
      }
    };

    execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    applePayEnabled,
    // canMakeApplePayPayments,
    setLoading,
    setWalletSupport,
    spreedlyPaymentProcessorType,
  ]);
};
